import React, { useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TablePagination from '@mui/material/TablePagination';
import { Select } from "antd";
import { CiSearch } from "react-icons/ci";
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { HiOutlineDotsVertical } from "react-icons/hi";
import { FaRegFlag } from "react-icons/fa6";
import { WiTime4 } from "react-icons/wi";

const EscalationTable = ({
  columns,
  data = [],
  totalRows,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  setSearchQuery,
  status,
  setStatus,
  priority,
  setPriority,
  user,
  setSelectedUser,
  selectedUser,
  onAssignAgent,
  onMarkResolved,
  onChangePriority,
  onViewDetails,
  isAssigning,
  isUpdatingPriority,
  isMarkingResolved,
  activeCustomerIds = [],
  activeCustomerIPs = [],
  websocketDataLoaded = false,
  messages = [],
  isAutoReloading = false,
  autoReloadEnabled = false,
  newEscalationsCount = 0,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  // Force re-render when messages or activeCustomerIPs changes
  React.useEffect(() => {
    // This will trigger a re-render when messages or activeCustomerIPs changes
  }, [messages, activeCustomerIPs]);

  const open = Boolean(anchorEl);
  const handleClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSearch = () => {
    if (!(isAssigning || isUpdatingPriority || isMarkingResolved)) {
      setSearchQuery(searchInput);
    }
  };

  // Helper function to check if a customer is active based on IP address
  // Using the same approach as ConversationHistory component
  const isCustomerActive = (row) => {
    // Try multiple possible field names for customer IP
    const customerIP = row.ip || 
                      row.customer_ip || 
                      row.active_customer?.ip || 
                      row.customer?.ip ||
                      row.user_ip ||
                      row.customer?.ip_address ||
                      row.user?.ip ||
                      row.user?.ip_address;
    
    // If no IP found, show as inactive (grey dot)
    if (!customerIP) {
      return false;
    }
    
    // If WebSocket data not loaded yet, show as inactive (grey dot)
    if (!websocketDataLoaded || !messages || messages.length === 0) {
      return false;
    }
    
    // Use the same approach as ConversationHistory: directly search for matching session
    // This is more efficient and avoids duplicate IP issues
    const allActiveSessions = messages.flatMap(item => item.active_sessions || []);
    const matchedSession = allActiveSessions.find(session => session?.ip === customerIP);
    
    return !!matchedSession;
  };

  return (
    <>
      <div className="flex flex-col md:flex-row flex-wrap  gap-2 justify-between">
        <div className="flex flex-row relative mt-3 h-10 gap-2 w-full md:w-96 ">
          <CiSearch
            className={`absolute left-3 size-5 top-2.5 ${
              (isAssigning || isUpdatingPriority || isMarkingResolved) ? 'opacity-50' : ''
            }`}
            style={{ color: "#A3A3A3" }}
          />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className={`md:w-96 w-full rounded-md border-2 pl-10 text-sm font-normal ${
              (isAssigning || isUpdatingPriority || isMarkingResolved) 
                ? 'border-gray-300 bg-gray-100 cursor-not-allowed' 
                : 'border-gray-200'
            }`}
            type="text"
            placeholder="Search by Ip, Region and Country..."
            style={{ color: "#000000" }}
            disabled={isAssigning || isUpdatingPriority || isMarkingResolved}
          />
        </div>
        <div className="flex flex-wrap flex-row gap-2 items-center">
          <Select
            className="w-32 h-10"
            value={status}
            onChange={(value) => {
              setStatus(value);
            }}
            options={[
              { value: "all", label: "All Status" },
              { value: "pending", label: "Pending" },
              { value: "resolved", label: "Resolved" },
              { value: "in_progress", label: "In Progress" },
            ]}
            disabled={isAssigning || isUpdatingPriority || isMarkingResolved}
          />

          <Select
            className="w-32 h-10"
            value={priority}
            onChange={(value) => {
              setPriority(value);
            }}
            options={[
              { value: "all", label: "All Priority" },
              { value: "high", label: "High" },
              { value: "medium", label: "Medium" },
              { value: "low", label: "Low" },
            ]}
            disabled={isAssigning || isUpdatingPriority || isMarkingResolved}
          />
          <Select
            className="w-32 h-10"
            value={selectedUser}
            onChange={(value) => setSelectedUser(value)}
            options={[
              { value: "all", label: "All Agents" },
              ...user.map((username) => ({
                value: username,
                label: username.charAt(0).toUpperCase() + username.slice(1),
              })),
            ]}
            disabled={isAssigning || isUpdatingPriority || isMarkingResolved}
          />

        </div>
      </div>

      <TableContainer component={Paper} elevation={0} className="relative">
        {(isAssigning || isUpdatingPriority || isMarkingResolved) && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 border-2 border-[#c5003e] border-t-transparent rounded-full animate-spin"></div>
              <span className="text-[#c5003e] font-medium">Processing...</span>
            </div>
          </div>
        )}
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              {columns.map((col, index) => (
                <TableCell key={col.header}>
                  <p className="font-medium text-[#71717A]">{col.header}</p>
                </TableCell>
              ))}
              <TableCell>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data.length > 0 ? (
              data.map((row, rowIndex) => {
                const isActive = isCustomerActive(row);
                return (
                <TableRow key={`${row.ticket_id}-${rowIndex}-${messages.length}-${isActive}`}>
                  {columns.map((col, colIndex) => (
                    <TableCell key={`${rowIndex}-${colIndex}`}>
                      {col.accessor === "Customer" ? (
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-black">{row.username}</span>
                            <div className="flex items-center gap-1">
                              <div className={`w-3 h-3 rounded-full ${
                                isActive 
                                  ? 'bg-green-500 animate-pulse' 
                                  : 'bg-gray-400'
                              }`} 
                              title={isActive ? 'Online' : 'Offline'}
                              style={{
                                backgroundColor: isActive ? '#10b981' : '#9ca3af',
                                animation: isActive ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none'
                              }}
                              ></div>
                            </div>
                          </div>
                          <span className="text-sm text-gray-500">
                            {row.email}
                          </span>
                        </div>
                      ) : col.accessor === "status" ? (
                        <span
                          className={`p-1 rounded-full text-sm font-medium border-2 flex flex-row items-center gap-1 justify-center
                            ${row.status === "pending"
                              ? "bg-[#FFFBEB] text-[#B45309] border-[#FDE68A]"
                              : row.status === "in_progress"
                                ? "bg-[#BFDBFE] text-[#1D4ED8] border-[#BFDBFE]"
                                : row.status === "resolved"
                                  ? "bg-[#F0FDF4] text-[#15803D] border-[#BBF7D0]"
                                  : ""
                            }`}
                        >
                          <span className="text-md font-extrabold"><WiTime4 /></span>{row.status}
                        </span>
                      ) : col.accessor === "priority" ? (
                        <span
                          className={`px-2 py-1 rounded-xl text-sm font-medium border-2 flex flex-row items-center gap-1 justify-center
                            ${row.priority === "high"
                              ? "bg-[#FECACA] text-[#B91C1C] border-[#FECACA]"
                              : row.priority === "medium"
                                ? "bg-[#FED7AA] text-[#C2410C] border-[#FED7AA]"
                                : row.priority === "low"
                                  ? "bg-[#F0FDF4] text-[#15803D] border-[#BBF7D0]"
                                  : ""
                            }`}
                        >
                          <span className="text-xs font-extrabold"><FaRegFlag /></span>{row.priority}
                        </span>
                      ) : col.accessor === "date" ? (
                        <div className="flex flex-col">
                          <span className="font-bold text-black">
                            {row.date ? new Date(row.date).toLocaleDateString() : 
                             row.created_at ? new Date(row.created_at).toLocaleDateString() : 
                             "N/A"}
                          </span>
                          <span className="text-sm text-gray-500">
                            {row.date ? new Date(row.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 
                             row.created_at ? new Date(row.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : 
                             ""}
                          </span>
                        </div>
                      ) : col.accessor === "agent" ? (
                        <span
                          className={`px-3 py-1 rounded-xl text-sm font-medium ${row.assign_to
                            ? "text-[#374151]"
                            : "bg-[#F9FAFB] text-[#374151] border-2 border-[#E5E7EB]"
                            }`}
                        >
                          {row.assign_to || "Unassigned"}
                        </span>
                      ) : typeof col.accessor === "function" ? (
                        col.accessor(row)
                      ) : (
                        row[col.accessor]
                      )}
                    </TableCell>
                  ))}
                  <TableCell key={rowIndex}>
                    <div>
                      <button
                        id={`button-${rowIndex}`}
                        aria-controls={open ? `menu-${rowIndex}` : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        onClick={(event) => handleClick(event, row)}
                        className={`p-2 rounded-lg ${
                          (isAssigning || isUpdatingPriority || isMarkingResolved)
                            ? 'bg-gray-200 cursor-not-allowed opacity-50'
                            : 'bg-[#F4F4F4] hover:bg-gray-300'
                        }`}
                        disabled={isAssigning || isUpdatingPriority || isMarkingResolved}
                      >
                        <HiOutlineDotsVertical />
                      </button>
                      <Menu
                        id={`menu-${rowIndex}`}
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        MenuListProps={{
                          'aria-labelledby': 'basic-button',
                        }}
                      >
                        <MenuItem>
                          <button
                            onClick={() => {
                              onViewDetails(selectedRow.ticket_id);
                            }}
                          >
                            View Details
                          </button>
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            handleClose();
                            if (selectedRow) {
                              onAssignAgent(selectedRow.ticket_id);
                            }
                          }}
                          disabled={isAssigning}
                          className={isAssigning ? 'opacity-50 cursor-not-allowed' : ''}
                        >
                          {isAssigning ? (
                            <div className="flex items-center gap-2">
                              <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
                              Assigning...
                            </div>
                          ) : (
                            'Assign Agent'
                          )}
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            handleClose();
                            if (selectedRow) {
                              onMarkResolved(selectedRow.ticket_id);
                            }
                          }}
                          disabled={isMarkingResolved}
                          className={isMarkingResolved ? 'opacity-50 cursor-not-allowed' : ''}
                        >
                          {isMarkingResolved ? (
                            <div className="flex items-center gap-2">
                              <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
                              Marking...
                            </div>
                          ) : (
                            'Mark as Resolved'
                          )}
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            handleClose();
                            if (selectedRow) {
                              // Trigger the parent function to show the priority modal
                              onChangePriority(selectedRow.ticket_id);
                            }
                          }}
                          disabled={isUpdatingPriority}
                          className={isUpdatingPriority ? 'opacity-50 cursor-not-allowed' : ''}
                        >
                          {isUpdatingPriority ? (
                            <div className="flex items-center gap-2">
                              <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
                              Updating...
                            </div>
                          ) : (
                            'Change Priority'
                          )}
                        </MenuItem>
                      </Menu>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer >

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalRows}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        disabled={isAssigning || isUpdatingPriority || isMarkingResolved || isAutoReloading}
      />
    </>
  );
};

export default EscalationTable;
